<template>
    <b-row class="mb-3">
        <b-card no-body bg-variant="white" class="w-100">
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0">규칙 그룹 검색하기</h5>

                    <div v-if="listLoading">
                        <div class="d-flex align-items-center" style="height: 36px">
                            <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                            <p class="d-inline-block mb-0">조회중...</p>
                        </div>
                    </div>
                    <div v-else class="d-flex align-items-center">
                        <b-button form="rule-group-search-form"
                                  type="reset"
                                  class="mr-2"
                                  :disabled="listLoading"
                        >
                            {{ $t('초기화') }}
                        </b-button>
                        <b-button
                            variant="primary"
                            :disabled="listLoading"
                            form="rule-group-search-form"
                            type="submit"
                        >
                            {{ $t('검색하기') }}
                        </b-button>
                    </div>
                </div>
            </template>

            <div class="p-3">
                <b-form id="rule-group-search-form"
                        autocomplete="new-password"
                        @submit.stop.prevent="searchData"
                        @reset.prevent="resetSearch"
                >
                    <b-row>
                        <b-col cols="12" class="mb-3 mb-xl-0">
                            <b-form-group
                                id="fieldset-input-group-name"
                                label="검색어"
                                label-for="input-group-name"
                                class="mb-0"
                            >
                                <b-form-input id="input-group-name"
                                              v-model="searchFormValue.search"
                                              :disabled="listLoading"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-form>

            </div>
        </b-card>


        <b-row class="mt-3">
            <b-col cols="12">
                <b-card
                    :class="[{ 'shadow-sm': searchResults || (!listLoading && searchResults) }, 'list-container']"
                    :bg-variant="!searchResults ? 'light' : 'white'"
                    no-body
                >
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 class="mb-0">규칙 그룹 목록</h6>
                            </div>
                            <div class="d-flex align-items-center">
                                <b-button variant="outline-primary" size="md"
                                          @click="openRuleGroupModal('add')">
                                    규칙 그룹 추가
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <div class="table-container">
                        <!-- DS 표 규칙: 행 44 고정 — 칸마다 한 줄, 넘치면 줄이고 전체는 title. 폭은 머리가 정한다 -->
                        <b-table
                            responsive
                            table-class="ds-table ds-table-fixed rule-group-list-table"
                            :items="searchResults"
                            :fields="tableColumns"
                            class="mb-0"
                            show-empty
                            :busy="listLoading"
                        >
                            <template #empty>
                                <PageEmptyComponent
                                    :text="$t('조회된 규칙 그룹이 없습니다.')"
                                    :loading="listLoading"
                                    :loadingText="$t('규칙 그룹 목록을 조회 중입니다.')"
                                    :error="listError"
                                    @retry="fetchData"
                                />
                            </template>
                            <!-- 메인 칸 = 상세 창 -->
                            <template #cell(name)="row">
                                <button type="button" class="ds-detail-link ds-ellipsis" :title="row.value"
                                        @click="openRuleGroupModal('detail', row.item)">{{ row.value }}</button>
                            </template>

                            <template #cell(composition)="row">
                                <!-- 목록 응답에 배열이 빠진 행이 있다(예전 row.value 는 빈 글자로 받아 0 이었다) -->
                                조건 {{ (row.item.conditions || []).length }} · 자식 그룹 {{ (row.item.child_groups || []).length }}
                            </template>

                            <template #cell(operator)="row">
                                <span v-if="row.value" class="ds-tag">{{ row.value }}</span>
                            </template>

                            <template #cell(updated_at)="row">
                                {{ get_date(row.value) }}
                            </template>

                            <template #head(detail)="data">
                                <span class="sr-only">{{ data.label }}</span>
                            </template>
                            <template #cell(detail)="row">
                                <button type="button" class="ds-row-icon-btn"
                                        :aria-label="`${row.item.name} 규칙 그룹 상세`" title="규칙 그룹 상세"
                                        @click="openRuleGroupModal('detail', row.item)">
                                    <AppIcon name="chevron-right"/>
                                </button>
                            </template>
                        </b-table>

                        <div class="pagination-container">
                            <b-pagination
                                ref="pagination"
                                v-model="pagination.currentPage"
                                :total-rows="pagination.totalRows"
                                :per-page="pagination.perPage"
                                first-number
                                last-number
                                :disabled="listLoading"
                                align="fill"
                                size="sm"
                                @change="(page)=>changeQuery({page})"
                            ></b-pagination>
                            <b-form-select
                                v-model="pagination.perPage"
                                class="ml-3 mb-3"
                                style="width: 100px"
                                size="sm"
                                :disabled="listLoading"
                                @change="(pageSize)=>changeQuery({pageSize})"
                            >
                                <b-form-select-option v-for="data in perPageOptions"
                                                      :key="`${data.text}-${data.value}`"
                                                      :value="data.value">{{ data.text }}
                                </b-form-select-option>
                            </b-form-select>

                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <RuleGroupModal ref="ruleGroupModal"
                        :detail-id="detailId"
                        @updateDetailData="updateDetailData"
                        @addData="initPage"
                        @deleteData="initPage"
        />

    </b-row>
</template>

<script>
import {listRuleGroups} from '@shared_utils/api/ruleEngine';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import {
    getDefaultPagination,
    onTooltipShow
} from '@shared/components/admin/pages/Management/common-data';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import PaginationPageMixin from '@shared/mixin/pagination-page-mixins';
import {listColumns, searchFormValue} from '@shared/components/admin/pages/Management/CampaignConnectRule/RuleGroup/data';
import RuleGroupModal from '@shared/components/admin/pages/Management/CampaignConnectRule/RuleGroup/RuleGroupModal.vue';

export default {
    name: 'ManageRuleGroup',
    components: {
        RuleGroupModal,
        PageEmptyComponent,
    },
    mixins: [PaginationPageMixin({searchForm: searchFormValue, pagination: getDefaultPagination(1, 20)})],
    props: {
        currentTab: {
            type: Number,
            default: 0
        }
    },

    data() {
        return {
            tableColumns: listColumns,
            detailId: null,
            listError: '',
        };
    },
    watch: {
        '$route': {
            handler() {
                if (this.$route.query.tab === 'ruleGroup') {
                    this.setSearchDataFormQuery();
                    this.fetchData();
                }
            },
            // 직접 URL/콘솔 고급 메뉴 진입은 route 변경이 없어 handler 가 안 돌므로 마운트 시에도 한 번 판정한다
            immediate: true
        }
    },
    methods: {
        onTooltipShow,
        async searchData() {
            this.pagination.currentPage = 1;
            const checkSameQuery = await this.changeQuery({});
            if (checkSameQuery) {
                await this.fetchData();
            }
        },
        initPage() {
            this.resetForm();
            this.searchData();
        },
        async fetchData() {
            try {
                this.listLoading = true;
                this.listError = '';

                const res = await listRuleGroups({
                    params: {
                        ...this.searchFormValue,
                        page: this.pagination.currentPage,
                        per_page: this.pagination.perPage,
                    },
                });

                if (res.data == null) {
                    this.searchResults = [];
                    this.pagination.totalRows = 0;
                    return;
                }
                const {rule_groups, total, current_page} = res.data;

                this.searchResults = rule_groups.map((item, index) => {
                    const data = {
                        ...item,
                        value: JSON.stringify(item.value),
                        // 번호는 응답 페이지 기준 — 새로고침 직후엔 페이지 버튼이 응답 전에 잠시 1 로 깎는다
                        index: this.pagination.perPage * ((Number(current_page) || 1) - 1) + index + 1,
                    };
                    return data;
                });

                this.pagination.totalRows = total;
                this.pagination.currentPage = null;
                setTimeout(() => {
                    this.pagination.currentPage = Number(current_page);
                });
            } catch (error) {
                // 실패를 '조회된 규칙 그룹이 없습니다'로 보이지 않게 이전 결과를 비우고 표 안에 오류를 띄운다
                this.searchResults = [];
                this.pagination.totalRows = 0;
                if (error?.response?.status === 401) return;
                this.listError = `규칙 그룹 목록을 불러오지 못했습니다. ${getErrorMessage(error, '')}`.trim();
                this.$EventBus.$emit('toast-failure', this.$t('검색 오류'), '규칙 그룹 검색 도중 오류가 발생했습니다. 잠시후 재시도 해주세요.');
            } finally {
                this.listLoading = false;
            }
        },
        async openRuleGroupModal(mode, item) {
            if (mode === 'add') {
                this.detailId = null;
                this.$refs.ruleGroupModal.changeMode(mode);
                this.$bvModal.show('rule-group-detail-modal');
                return;
            }


            this.detailId = item.id;
            this.$refs.ruleGroupModal.changeMode(mode);
            this.$bvModal.show('rule-group-detail-modal');

        },
        updateDetailData(updatedItem) {
            const idx = this.searchResults.findIndex((item) => {
                return item.id === updatedItem.id;
            });
            // 번호는 목록에서 매긴 값을 그대로(다시 매기면 여러 페이지일 때 그 행만 페이지 안 번호로 바뀌었다)
            this.$set(this.searchResults, idx, {...updatedItem, index: this.searchResults[idx]?.index});
        }
    }
};
</script>

<style lang="scss">
/* 고정 열 합 532(No. 64 · 구성 160 · 연산자 88 · 수정일시 160 · 상세 60) + 규칙 그룹명 ~228. 조건 · 캠페인 규칙 탭과 같은 기준
   (1366 메뉴 펼침 표 폭 약 1062 안) — 그보다 좁으면 가로 스크롤 */
.rule-group-list-table {
    min-width: 760px;
}
</style>
